import type { ChatMessageData } from '@ps/api/types';

import { type ChatMessage, ChatMessageType } from '../types/ChatMessage';
import { ChatSessionType } from '../types/ChatSessionType';
import type { ChatUser } from '../types/ChatUser';

export function privateMessageEventDataToChatMessage(
  remoteMsg: ChatMessageData,
  users?: ChatUser[],
): ChatMessage {
  let quoteMsg: ChatMessage | undefined;
  try {
    quoteMsg = remoteMsg.quoteMsg
      ? (JSON.parse(remoteMsg.quoteMsg) as ChatMessage)
      : undefined;
  } catch (e) {
    console.error('convert quote message error', remoteMsg, e);
  }

  const fromUser = users?.find(item => item.userId === remoteMsg.fromUserId);
  const toUser = users?.find(item => item.userId === remoteMsg.toUserId);
  const content = safeParseMessageContent(remoteMsg);
  const isAiMessage = parseJSONIsAiMessage(remoteMsg?.content || '');
  const type = remoteMsg.messageType as ChatMessageType;

  return {
    messageId: remoteMsg.messageId,
    clientId: remoteMsg.clientId,
    from: {
      userId: remoteMsg.fromUserId,
      name: remoteMsg.fromNickname || fromUser?.name || '',
      avatar: remoteMsg.fromAvatar || fromUser?.avatar || '',
    },
    to: {
      userId: remoteMsg.toUserId,
      name: remoteMsg.toNickname || toUser?.name || '',
      avatar: remoteMsg.toAvatar || toUser?.avatar || '',
    },
    roomId: remoteMsg.sessionId,
    sessionType:
      (remoteMsg.sessionType as ChatSessionType | undefined) ??
      (type === ChatMessageType.Post
        ? ChatSessionType.PrivatePromotional
        : ChatSessionType.Private),
    sessionId: remoteMsg.sessionId,
    chatSessionId: remoteMsg.chatSessionId,
    time: new Date(remoteMsg.createdAt).getTime(),
    type,
    customType: undefined,
    text: remoteMsg.text,
    content,
    quoteMsg,
    isAiMessage,
  } as ChatMessage;
}

function parseJSONSerializedContent(
  msg: ChatMessageData,
  raw: string,
): Record<string, unknown> {
  const obj = JSON.parse(raw) as Record<string, unknown>;
  switch (msg.messageType) {
    case ChatMessageType.Text:
      return 'text' in obj ? obj : { text: msg.text ?? '' };
    case ChatMessageType.Picture:
      return 'imageContent' in obj
        ? ((obj.imageContent as Record<string, unknown>) ?? {})
        : obj;
    case ChatMessageType.Video:
      return 'videoContent' in obj
        ? ((obj.videoContent as Record<string, unknown>) ?? {})
        : obj;
    case ChatMessageType.Order:
      return 'orderContent' in obj
        ? ((obj.orderContent as Record<string, unknown>) ?? {})
        : obj;
    case ChatMessageType.Product:
      return 'productContent' in obj
        ? ((obj.productContent as Record<string, unknown>) ?? {})
        : obj;
  }
  return obj;
}

function parseJSONIsAiMessage(msg: string): boolean {
  try {
    const obj = JSON.parse(msg) as { isAiMessage?: unknown };
    if (obj && typeof obj === 'object' && 'isAiMessage' in obj) {
      return obj.isAiMessage === true;
    }
  } catch (e) {
    console.error('error parse isAiMessage: ', e);
  }
  return false;
}

function safeParseMessageContent(msg: ChatMessageData): unknown {
  if (msg.content == null) return {};

  try {
    if (typeof msg.content === 'string') {
      if (msg.content.startsWith('{')) {
        let content = parseJSONSerializedContent(msg, msg.content);
        if (
          'text' in content &&
          typeof content.text === 'string' &&
          content.text.startsWith('{')
        ) {
          content = JSON.parse(content.text) as Record<string, unknown>;
        }
        return content;
      }

      if (
        msg.messageType === ChatMessageType.Text ||
        msg.messageType === ChatMessageType.SystemMessage
      ) {
        return { text: msg.content || msg.text };
      }
    }
    return msg.content;
  } catch (e) {
    console.error('error parse message content: ', e);
  }
  return {};
}
